import { supabase } from "@/integrations/supabase/client";
import type {
  EdgeFunctionLog,
  EdgeFunctionLogFilters,
  EdgeFunctionLogsResponse,
  EdgeFunctionLogStats,
} from "@/types/edge-function-logs";

function extractActionResource(log: any): { action: string | null; resource: string | null } {
  const sources = [log.request_body, log.request_query];
  let action: string | null = null;
  let resource: string | null = null;

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    if (!action && typeof source.action === "string") action = source.action;
    if (!resource && typeof source.resource === "string") resource = source.resource;
  }

  return { action, resource };
}

function applyFilters(query: any, filters?: EdgeFunctionLogFilters) {
  if (filters?.search) {
    query = query.ilike("function_name", `%${filters.search}%`);
  }

  if (filters?.function_name && filters.function_name !== "all") {
    query = query.eq("function_name", filters.function_name);
  }

  if (filters?.unresolved_only) {
    // Only failed rows can be "unresolved" — this implicitly constrains status to "failed"
    // regardless of the status filter, since successful/in-flight rows never need resolving.
    query = query.eq("status", "failed").is("resolved_at", null);
  } else if (filters?.status && filters.status !== "all") {
    query = query.eq("status", filters.status);
  }

  query = applyCallerFilters(query, filters);

  return applyDateRange(query, filters);
}

const CALLER_EMBED = "profiles!edge_function_logs_caller_id_fkey";

// Filtering on the caller's email needs an inner join so non-matching logs are dropped, not just their embed.
const filtersByEmail = (filters?: EdgeFunctionLogFilters) => !filters?.guest_only && !!filters?.caller_email;

function callerSelect(columns: string, filters?: EdgeFunctionLogFilters) {
  return `${columns}, ${CALLER_EMBED}${filtersByEmail(filters) ? "!inner" : ""}(email)`;
}

function applyCallerFilters(query: any, filters?: EdgeFunctionLogFilters) {
  if (filters?.guest_only) return query.is("caller_id", null);
  if (filtersByEmail(filters)) return query.ilike("profiles.email", `%${filters?.caller_email}%`);
  return query;
}

// A collapsed failure spans created_at..last_occurred_at, so match on overlap.
function applyDateRange(query: any, filters?: EdgeFunctionLogFilters) {
  if (filters?.date_from) {
    query = query.gte("last_occurred_at", filters.date_from);
  }

  if (filters?.date_to) {
    query = query.lte("created_at", filters.date_to);
  }

  return query;
}

export class EdgeFunctionLogsService {
  static async getEdgeFunctionLogs(
    filters?: EdgeFunctionLogFilters,
    pageSize: number = 50,
    offset: number = 0
  ): Promise<EdgeFunctionLogsResponse> {
    try {
      let query = supabase
        .from("edge_function_logs")
        .select(callerSelect("*", filters), { count: "exact" })
        .order("last_occurred_at", { ascending: false });

      query = applyFilters(query, filters);

      const { data, count, error } = await query.range(offset, offset + pageSize - 1);

      if (error) {
        throw new Error(`Failed to fetch edge function logs: ${error.message}`);
      }

      const mappedData: EdgeFunctionLog[] = (data || []).map((log: any) => ({
        ...log,
        caller_email: log.profiles?.email || null,
        ...extractActionResource(log),
      }));

      return {
        data: mappedData,
        count: count || 0,
      };
    } catch (error: any) {
      console.error("Error fetching edge function logs:", error);
      throw error;
    }
  }

  static async getEdgeFunctionLog(id: string): Promise<EdgeFunctionLog> {
    try {
      const { data, error } = await supabase
        .from("edge_function_logs")
        .select(callerSelect("*"))
        .eq("id", id)
        .single();

      if (error) {
        throw new Error(`Failed to fetch edge function log: ${error.message}`);
      }

      return {
        ...data,
        caller_email: data.profiles?.email || null,
      };
    } catch (error: any) {
      console.error("Error fetching edge function log:", error);
      throw error;
    }
  }

  static async getEdgeFunctionLogStats(filters?: EdgeFunctionLogFilters): Promise<EdgeFunctionLogStats> {
    try {
      let query = supabase
        .from("edge_function_logs")
        .select(filtersByEmail(filters) ? callerSelect("resolved_at, occurrence_count", filters) : "resolved_at, occurrence_count", {
          count: "exact",
        })
        .eq("status", "failed");

      if (filters?.function_name && filters.function_name !== "all") {
        query = query.eq("function_name", filters.function_name);
      }

      query = applyCallerFilters(query, filters);
      query = applyDateRange(query, filters);

      const { data: failedLogs, count: failureCount, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      const stats: EdgeFunctionLogStats = { failures: failureCount || 0, occurrences: 0, unresolved: 0 };

      // A collapsed failure is one row standing for occurrence_count calls.
      failedLogs?.forEach((log) => {
        stats.occurrences += log.occurrence_count ?? 1;
        if (!log.resolved_at) stats.unresolved += 1;
      });

      return stats;
    } catch (error: any) {
      console.error("Error fetching edge function log stats:", error);
      throw error;
    }
  }

  static async resolveLog(id: string): Promise<void> {
    const { error } = await supabase
      .from("edge_function_logs")
      .update({ resolved_at: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      throw new Error(`Failed to resolve log: ${error.message}`);
    }
  }

  static async exportToCSV(filters?: EdgeFunctionLogFilters): Promise<string> {
    try {
      let query = supabase.from("edge_function_logs").select(filtersByEmail(filters) ? callerSelect("*", filters) : "*");
      query = applyFilters(query, filters);

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to export logs: ${error.message}`);
      }

      const headers = [
        "function_name",
        "status",
        "caller_id",
        "is_impersonated",
        "duration_ms",
        "error_message",
        "occurrence_count",
        "created_at",
        "last_occurred_at",
        "completed_at",
        "resolved_at",
      ];

      if (!data || data.length === 0) {
        return `${headers.join(",")}\n`;
      }

      const rows = data.map((log: any) =>
        headers.map((header) => {
          const value = log[header];
          const stringValue = String(value ?? "");
          return `"${stringValue.replace(/"/g, '""')}"`;
        }).join(",")
      );

      return `${headers.join(",")}\n${rows.join("\n")}`;
    } catch (error: any) {
      console.error("Error exporting edge function logs:", error);
      throw error;
    }
  }
}
